import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { AnchoredTooltip } from "@/components/common/AnchoredTooltip";
import { cn } from "@/lib/utils";

const SHOW_DELAY_MS = 220;
const HIDE_DELAY_MS = 120;

interface HoverTooltipProps {
  content?: ReactNode;
  className?: string;
  children: ReactNode;
}

/**
 * Portal hover tooltip. Escapes clipped table cells the same way surcharge
 * code tooltips do. The bubble points at its trigger and stays on-screen.
 */
export function HoverTooltip({ content, className, children }: HoverTooltipProps) {
  const hasTooltip = content != null && content !== "";
  const ref = useRef<HTMLDivElement>(null);
  const showTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return () => {
      if (showTimer.current) clearTimeout(showTimer.current);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  const show = useCallback(() => {
    if (!hasTooltip) return;
    if (hideTimer.current) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    if (open) return;
    showTimer.current = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
  }, [hasTooltip, open]);

  const hide = useCallback(() => {
    if (showTimer.current) {
      clearTimeout(showTimer.current);
      showTimer.current = null;
    }
    hideTimer.current = setTimeout(() => setOpen(false), HIDE_DELAY_MS);
  }, []);

  if (!hasTooltip) {
    return <>{children}</>;
  }

  return (
    <div
      ref={ref}
      className={cn("inline-flex", className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && (
        <AnchoredTooltip
          anchorRef={ref}
          onMouseEnter={show}
          onMouseLeave={hide}
        >
          {content}
        </AnchoredTooltip>
      )}
    </div>
  );
}
